import { type ReactNode, useEffect } from 'react';
import { supabase } from '@/shared/services/supabaseClient';
import { useAuthStore } from '@/shared/stores/authStore';
import { useOrgStore } from '@/shared/stores/orgStore';
import type { UserRole, Organization, UserProfile } from '@/shared/types/common.types';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Fetch the user's profile, org membership, and organization from Supabase.
 * Populates authStore and orgStore.
 */
async function loadUserContext(userId: string): Promise<void> {
  const authStore = useAuthStore.getState();
  const orgStore = useOrgStore.getState();

  // Fetch profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('user_id, email, full_name, avatar_url')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .limit(1)
    .single();

  if (profile?.email) {
    authStore.setUser({
      id: profile.user_id,
      email: profile.email,
      full_name: profile.full_name ?? null,
      avatar_url: profile.avatar_url ?? null,
    } satisfies UserProfile);
  }

  // Fetch organization membership (first active membership)
  const { data: membership } = await supabase
    .from('organization_members')
    .select('organization_id, role')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .limit(1)
    .single();

  if (membership) {
    authStore.setOrganizationId(membership.organization_id);
    authStore.setRole(membership.role as UserRole);

    // Fetch organization details
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', membership.organization_id)
      .single();

    if (orgError) {
      console.error('[Auth] Failed to load organization:', {
        code: orgError.code,
        message: orgError.message,
        hint: orgError.hint,
        orgId: membership.organization_id,
      });
    }

    if (org) {
      orgStore.setOrganization(org as unknown as Organization);
    }
  }
}

/**
 * AuthProvider — wired to Supabase onAuthStateChange.
 * On mount: checks for existing session.
 * On auth change: loads user context (profile, org, role) into stores.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const isLoading = useAuthStore((s) => s.isLoading);

  useEffect(() => {
    const authStore = useAuthStore.getState();

    // 1. Check for existing session
    void supabase.auth.getSession().then(({ data: { session } }) => {
      authStore.setSession(session);

      if (session?.user) {
        void loadUserContext(session.user.id).catch((err: unknown) => {
          console.error('Failed to load user context:', err);
        }).finally(() => {
          authStore.setIsLoading(false);
        });
      } else {
        authStore.setIsLoading(false);
      }
    });

    // 2. Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      authStore.setSession(session);

      if (session?.user) {
        void loadUserContext(session.user.id).catch((err: unknown) => {
          console.error('Failed to load user context:', err);
        }).finally(() => {
          authStore.setIsLoading(false);
        });
      } else {
        // Signed out — clear stores
        authStore.setUser(null);
        authStore.setOrganizationId(null);
        authStore.setRole(null);
        authStore.setIsLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}
